/**
 * Unités d'article et précision de quantité — source unique.
 *
 * Le carton (`box`) se compte en nombres ENTIERS : une quantité décimale
 * (« 1,5 carton ») n'a pas de sens métier et doit être refusée avec un
 * message d'erreur. Toutes les autres unités — y compris la pièce (`pcs`),
 * par décision produit — acceptent jusqu'à 3 décimales.
 *
 * Cette règle était auparavant dupliquée et incohérente : un `Set` codé en
 * dur (`INTEGER_UNITS`) dans le seul formulaire de vente, ignoré par les
 * achats, la caisse et le mobile. Elle vit désormais ici, importée par le
 * web, le mobile et les schémas Zod, pour ne plus jamais diverger.
 */

/**
 * Unités proposées à la création d'un article (web + mobile).
 * Les libellés traduits vivent sous la clé i18n `articles.units.*`.
 */
export const ARTICLE_UNITS = ['pcs', 'kg', 'm', 'l', 'm2', 'box'] as const;
export type ArticleUnit = (typeof ARTICLE_UNITS)[number];

/**
 * Unités qui ne se comptent qu'en nombres entiers (pas de décimales).
 * `box` = carton. La pièce (`pcs`) n'en fait volontairement PAS partie :
 * les quantités fractionnaires de pièces sont autorisées à la saisie.
 */
export const WHOLE_UNIT_CODES: ReadonlySet<string> = new Set(['box']);

/** Unité par défaut d'un article quand aucune n'est renseignée. */
export const DEFAULT_ARTICLE_UNIT = 'pcs';

/**
 * `true` si l'unité impose des quantités entières (cartons).
 * Une unité inconnue/personnalisée est considérée mesurable (décimales OK) —
 * on ne bloque jamais une saisie sur une unité qu'on ne connaît pas.
 */
export function isWholeUnitQuantity(unit: string | null | undefined): boolean {
  return unit != null && WHOLE_UNIT_CODES.has(unit);
}

/**
 * Nombre de décimales autorisées pour une quantité dans cette unité :
 * `0` (entier) pour les unités entières, `3` (millimes) sinon.
 * Utile pour le formatage d'affichage. NE PAS l'utiliser comme `maxDecimals`
 * d'un champ de saisie libre : `maxDecimals: 0` avale le séparateur et
 * transforme silencieusement « 5,7 » en « 57 » (erreur ×10). Pour la saisie,
 * laisser 3 décimales et valider via {@link isQuantityValidForUnit}.
 */
export function quantityDecimals(unit: string | null | undefined): number {
  return isWholeUnitQuantity(unit) ? 0 : 3;
}

/**
 * Valide une quantité pour une unité donnée.
 *
 * - Quantité vide / non finie (`NaN`) → considérée valide ici : la règle
 *   « quantité > 0 » est gérée séparément (`.positive()`), on ne veut pas
 *   empiler deux messages.
 * - Unité mesurable → toute quantité finie passe.
 * - Unité entière → seuls les entiers passent.
 */
export function isQuantityValidForUnit(
  quantity: number,
  unit: string | null | undefined,
): boolean {
  if (!Number.isFinite(quantity)) return true;
  if (!isWholeUnitQuantity(unit)) return true;
  return Number.isInteger(quantity);
}
